import React from 'react';
import styled from '@benzinga/themetron';
import { BenzingaIndiaLogo, BenzingaLogo } from '@benzinga/logos-ui';
import FooterLink from '../../FooterLink';
import SocialIcon from '../../SocialIcon';
import AppStoreButton from '../../AppStoreButton';
import { map, values } from 'ramda';
import { isMobile } from '@benzinga/device-utils';
import { FooterStickyNav } from '../../FooterStickyNav';
import { useAmp } from 'next/amp';

import {
  aboutBenzinga,
  AppStore,
  copyrightSection,
  copyrightSectionIndia,
  FooterLink as IFooterLink,
  partnersAndContributors,
  popularChannels,
  socialSites,
  toolsAndFeatures,
  appStores,
  SocialSites,
} from '../../footerEntity';
import { appEnvironment, appName } from '@benzinga/utils';

import { NavigationFooterProps } from '../..';

export const DefaultFooter: React.FC<NavigationFooterProps> = ({
  showAppList = true,
  showFooterLinksTable = true,
  showStickyFooter,
  socialMedia = {},
}) => {
  const isAmp = useAmp();
  const year = new Date().getFullYear();
  const isIndiaApp = appEnvironment().isApp(appName.india);
  const footerLogo = isIndiaApp ? <BenzingaIndiaLogo variant="light" /> : <BenzingaLogo />;
  const copyrightLinks = isIndiaApp ? copyrightSectionIndia : copyrightSection;

  const renderFooterLinks = (links: IFooterLink[]) =>
    links.map(({ label, url }) => <FooterLink key={label} label={label} url={url} />);

  const socialSitesList =
    Object.keys(socialMedia).length > 0
      ? Object.keys(socialMedia).map((site: string) => {
          const socialSitesProps = {
            ...socialSites[site],
            ...socialMedia[site],
          };

          return <SocialIcon {...socialSitesProps} isAmp={isAmp} key={socialSitesProps.name} />;
        })
      : Object.keys(socialSites).map((site: string) => {
          const socialSitesProps = socialSites[site];
          return <SocialIcon {...socialSitesProps} isAmp={isAmp} key={socialSitesProps.name} />;
        });

  const AppList = map(
    (appStore: AppStore) => (
      <AppStoreButton icon={appStore.icon} key={appStore.name} name={appStore.name} storeUrl={appStore.link} />
    ),
    values(appStores),
  );

  return (
    <>
      <div className={isMobile() ? 'footer has-sticky-nav' : 'footer'}>
        <div className="footer-top-section">
          <div className="benzinga-links-section">
            <div className="logo-and-socials-wrapper">
              {footerLogo}
              <div className="social-sites-list">{socialSitesList}</div>
            </div>
            {showAppList && <div className="app-store-wrapper">{AppList}</div>}
          </div>
          {showFooterLinksTable && (
            <Row className="footer-row">
              <StyledColumn>
                <h3>Popular Channels</h3>
                <ul>{renderFooterLinks(popularChannels)}</ul>
              </StyledColumn>
              <StyledColumn>
                <h3>Tools &amp; Features</h3>
                <ul>{renderFooterLinks(toolsAndFeatures)}</ul>
              </StyledColumn>
              <StyledColumn>
                <h3>Partners &amp; Contributors</h3>
                <ul>{renderFooterLinks(partnersAndContributors)}</ul>
              </StyledColumn>
              <StyledColumn>
                <h3>About Benzinga</h3>
                <ul>{renderFooterLinks(aboutBenzinga)}</ul>
              </StyledColumn>
            </Row>
          )}
        </div>
        <div className="footer-bottom-section footer-bottom-common">
          <ul>{renderFooterLinks(copyrightLinks)}</ul>
          <div className="copyright-section text-center w-full whitespace-nowrap mt-2">
            &copy; {year} Benzinga | All Rights Reserved
          </div>
        </div>
      </div>
      {isMobile() && showStickyFooter && <FooterStickyNav />}
    </>
  );
};

export default DefaultFooter;

const Row = styled.div`
  &.footer-row {
    display: grid;
    grid-auto-flow: column;
    grid-gap: 20px;
    width: 100%;
    padding-top: 0.5rem;
    padding-bottom: 2rem;
    padding-left: 1rem;
    padding-right: 1rem;
    margin-left: auto;
    margin-right: auto;

    @media screen and (min-width: 480px) {
      max-width: 480px;
    }

    @media screen and (min-width: 768px) {
      padding-left: 4rem;
      padding-right: 4rem;
      max-width: 100%;
      grid-gap: 30px;
    }

    @media screen and (max-width: 1000px) {
      grid-auto-flow: row;
      grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    }
  }
`;

const StyledColumn = styled.div`
  display: flex;
  flex-direction: column;
  text-align: left;

  h3 {
    display: flex;
    color: ${({ theme }) => theme.colorPalette.white};
    font-size: ${({ theme }) => theme.fontSize.xl};
    font-weight: ${({ theme }) => theme.fontWeight.semibold};
    margin: 0;

    @media screen and (min-width: 480px) {
      margin-top: 0;
    }

    @media screen and (min-width: 768px) {
      font-size: ${({ theme }) => theme.fontSize.lg};
      margin-top: 2rem;
    }
  }

  ul {
    list-style: none;
    padding: 0;
    li {
      font-size: ${({ theme }) => theme.fontSize.lg};

      @media screen and (min-width: 768px) {
        font-size: ${({ theme }) => theme.fontSize.base};
      }

      > a {
        color: ${({ theme }) => theme.colorPalette.gray200};

        &:hover {
          color: ${({ theme }) => theme.colorPalette.white};
        }
      }
    }
  }
`;
